import { Dispatch } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import { B2BSearchAutocomplete, IB2BSearchAutocompleteProps } from "halifax";
import {
  ICategorizedResponse,
  IResult,
  MegaMenuRegion,
  NonEmptyArray,
  StayTypesEnum,
} from "redmond";

import { IStoreState } from "../../../../../../reducers/types";
import { actions } from "../../../../actions";
import {
  getLocation,
  getLocationCategories,
  getLocationCategoriesLoading,
  getLocationSearchString,
  getStayType,
  getVacationRentalsLocation,
  getVacationRentalsLocationCategories,
} from "../../../../reducer";
import {
  AUTOCOMPLETE_RESULTS_GROUP_LABEL,
  HOTELS_WITH_VR_AUTOCMPLETE_RESULTS_LABEL,
  LC_AUTOCOMPLETE_RESULTS_GROUP_LABEL,
  PREMIUM_STAYS_AUTOCOMPLETE_RESULTS_GROUP_LABEL,
  VACATION_RENTALS_AUTOCOMPLETE_RESULTS_LABEL,
} from "../../textConstants";
import { MIN_BACKEND_AUTOCOMPLETE_INPUT_LENGTH } from "../../../constants";

interface IDispatchProps {
  setLocation: (location: IResult | null) => actions.ISetLocation;
  setVacationRentalsLocation: (
    location: IResult | null
  ) => actions.ISetVacationRentalsLocation;
  fetchPCLocationAutocomplete: (
    queryString: string
  ) => actions.IFetchPCLocationAutocomplete;
  fetchVRLocationAutocomplete: (
    queryString: string
  ) => actions.IFetchVRLocationAutocomplete;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => {
  return {
    setLocation: (location: IResult | null) =>
      dispatch(actions.setLocation(location)),
    setVacationRentalsLocation: (location: IResult | null) =>
      dispatch(actions.setVacationRentalsLocation(location)),
    fetchPCLocationAutocomplete: (queryString: string) =>
      dispatch(actions.fetchPCLocationAutocomplete(queryString)),
    fetchVRLocationAutocomplete: (queryString: string) =>
      dispatch(actions.fetchVRLocationAutocomplete(queryString)),
  };
};

interface IStateProps {
  value: IResult | null;
  rawLocationCategories: ICategorizedResponse[];
  rawVacationRentalsLocationCategories: ICategorizedResponse[];
  loading: boolean;
  callbackOnEmptyValue: true;
  groupHeadingText?: string;
  staysType?: StayTypesEnum;
  regions?: NonEmptyArray<MegaMenuRegion>;
}

interface IOwnProps {
  isLifestyleCollection?: boolean;
  includesLifestyleCollection?: boolean;
  includesVacationRentals?: boolean;
  overrideInputValue?: string;
}

export const mapStateToProps = (
  state: IStoreState,
  ownProps: IOwnProps
): IStateProps => {
  let loading = !!getLocationCategoriesLoading(state);
  const staysType = getStayType(state);

  return {
    value:
      staysType === StayTypesEnum.VacationRentals
        ? getVacationRentalsLocation(state)
        : getLocation(state),
    rawLocationCategories: getLocationCategories(state),
    rawVacationRentalsLocationCategories: getVacationRentalsLocationCategories(state),
    loading,
    callbackOnEmptyValue: true,
    groupHeadingText:
      !!getLocationSearchString(state) ||
        staysType === StayTypesEnum.Hotels
        ? undefined
        : staysType === StayTypesEnum.VacationRentals
        ? VACATION_RENTALS_AUTOCOMPLETE_RESULTS_LABEL
        : ownProps.includesVacationRentals
        ? HOTELS_WITH_VR_AUTOCMPLETE_RESULTS_LABEL
        : ownProps.isLifestyleCollection
        ? LC_AUTOCOMPLETE_RESULTS_GROUP_LABEL
        : ownProps.includesLifestyleCollection
        ? PREMIUM_STAYS_AUTOCOMPLETE_RESULTS_GROUP_LABEL
        : AUTOCOMPLETE_RESULTS_GROUP_LABEL,
    staysType,
    regions: state.premierCollectionSearch.megaMenuRegions,
  };
};

const mergeProps = (
  stateProps: IStateProps,
  dispatchProps: IDispatchProps,
  ownProps: IOwnProps
) => {
  const staysType = stateProps.staysType;
  const isVR = staysType === StayTypesEnum.VacationRentals;

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    fetchValueCategories: isVR
      ? dispatchProps.fetchVRLocationAutocomplete
      : dispatchProps.fetchPCLocationAutocomplete,
    valueCategories: isVR
      ? stateProps.rawVacationRentalsLocationCategories
      : stateProps.rawLocationCategories,
    setValue: (location: IResult | null) =>
      isVR
        ? dispatchProps.setVacationRentalsLocation(location)
        : dispatchProps.setLocation(location),
    minInputLength: MIN_BACKEND_AUTOCOMPLETE_INPUT_LENGTH,
  };
};

export const ConnectedLocationAutocomplete = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(
  B2BSearchAutocomplete as (
    props: IB2BSearchAutocompleteProps<IResult | null>
  ) => JSX.Element
);
